import genericMemo from "Common/componentUtils/genericMemo";
import sort from "Common/componentUtils/sort";
import useIsVisible from "Common/componentUtils/useIsVisible";
import withOverlaysProvider from "Components/overlaysContext/withOverlaysProvider";
import ProgressOverlay from "Components/progressOverlay";
import WelcomePanel from "Components/welcomePanel";
import classNames from "classnames";
import compact from "lodash/compact";
import map from "lodash/map";
import some from "lodash/some";
import VirtualScrollList from "rc-virtual-list";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import { progressOverlayStyle } from "../../constants";
import SidebarMenuItemComp from "../../sidebarMenuItem";
import Styled, { SIZES } from "./sidebarMenuList.inner.styled";
import styles from "./sidebarMenuList.module.css";
function SidebarMenuListInner(props) {
    var _a, _b;
    const { height, ref: observableRef } = useResizeDetector();
    const virtualScrollRef = useRef(null);
    const containerRef = useRef(null);
    const isVisible = useIsVisible(containerRef);
    const [groupsOpened, setGroupsOpened] = useState({
        mode: "defaultClosed",
        openedByUser: new Set(),
        closedByUser: new Set()
    });
    useEffect(() => {
        var _a;
        isVisible && ((_a = virtualScrollRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo(0));
    }, [isVisible]);
    const handleOpenedChanged = useCallback((key, open) => {
        setGroupsOpened(prev => {
            const openedByUser = new Set(prev.openedByUser);
            if (open)
                openedByUser.add(key);
            else
                openedByUser.delete(key);
            const closedByUser = new Set(prev.closedByUser);
            if (!open)
                closedByUser.add(key);
            else
                closedByUser.delete(key);
            return {
                ...prev,
                openedByUser,
                closedByUser
            };
        });
    }, []);
    const handleScroll = useCallback(() => {
        props.hidePopoverOverlays && props.hidePopoverOverlays();
    }, []);
    const sidebarMenuItems = useMemo(() => sort(compact(map(props.sidebarMenuItems, s => s ? s.filterAndSort(props.filterText, props.sortBy, props.sortOrder) : undefined)), props.sortBy, props.sortOrder), [props.sidebarMenuItems, props.filterText, props.sortBy, props.sortOrder]);
    useEffect(() => {
        setGroupsOpened(prev => {
            const newMode = props.filterText ? "defaultOpened" : "defaultClosed";
            return newMode === prev.mode
                ? prev
                : { mode: newMode, openedByUser: new Set(), closedByUser: new Set() };
        });
    }, [props.filterText]);
    const isSelected = useCallback((key) => Boolean(props.selectedItems && props.selectedItems.has(key)), [props.selectedItems]);
    const isActive = useCallback((key, childKeys, selected) => Boolean(props.activeItems && props.activeItems.has(key)) ||
        Boolean(childKeys.length &&
            props.selectedItems &&
            (selected || some(childKeys, childKey => { var _a; return (_a = props.selectedItems) === null || _a === void 0 ? void 0 : _a.has(childKey); }))), [props.activeItems, props.selectedItems]);
    const isOpened = useCallback((key, childKeys, active) => groupsOpened.openedByUser.has(key) ||
        ((groupsOpened.mode === "defaultOpened" || (childKeys.length > 0 && active)) &&
            !groupsOpened.closedByUser.has(key)), [groupsOpened.mode, groupsOpened.closedByUser, groupsOpened.openedByUser]);
    const handleItemPrepareToRender = useCallback((e) => {
        const item = props.onItemPrepareToRender ? props.onItemPrepareToRender(e) : e;
        if (item.isBtn)
            return item;
        item.selected = isSelected(item.key);
        item.active = isActive(item.key, item.childKeys, item.selected);
        item.opened = isOpened(item.key, item.childKeys, item.active);
        if (item.subItems && item.opened) {
            item.subItems = map(item.subItems, handleItemPrepareToRender);
        }
        return item;
    }, [props.onItemPrepareToRender, isSelected, isActive, isOpened]);
    const itemRender = useCallback((item) => (React.createElement(SidebarMenuItemComp, { key: item.key, depth: 0, data: item, selected: item.selected, active: item.active, opened: item.opened, subItems: item.subItems, className: classNames(item.isBtn && "clickable", item.isBtn && props.hasOutsideIndents && "ml-12"), onOpenedChanged: !item.isBtn ? handleOpenedChanged : undefined, onSelectChanged: !item.isBtn ? props.onSelectChanged : undefined, onItemClick: props.onItemClick, onSubmenuShown: props.onSubmenuShown, onItemDoubleClick: props.onItemDoubleClick, filterText: !item.isBtn ? props.filterText : undefined, actionIconColor: props.actionIconColor, margin: props.margin })), [
        handleOpenedChanged,
        props.onSelectChanged,
        props.onItemClick,
        props.actionIconColor,
        props.hasOutsideIndents,
        props.filterText,
        props.margin
    ]);
    const itemVirtualRender = useCallback((item) => (React.createElement("div", { className: classNames("virtual-row") }, itemRender(handleItemPrepareToRender(item)))), [handleItemPrepareToRender, itemRender, props.hasOutsideIndents]);
    return (React.createElement(ProgressOverlay, { show: props.inProgress, className: classNames("overflow-hidden", props.hasOutsideIndents && "pt-16 pb-0_5", props.className), overlayStyle: props.bgColorDefault !== "transparent" ? progressOverlayStyle : undefined },
        React.createElement(Styled.NavItemsContainer, { ref: containerRef, bgColorDefault: props.bgColorDefault, bgColorActive: props.bgColorActive, colorDefault: props.colorDefault, colorHover: props.colorHover, colorActive: props.colorActive, bgColorHover: props.bgColorHover, className: classNames("overflow-hidden", props.sidebarMenuButton || props.header ? "grid-rows-auto-1fr" : "stretched-to-fit"), smallMargins: props.smallMargins }, (_a = props.header) === null || _a === void 0 ? void 0 :
            _a.call(props),
            props.sidebarMenuButton &&
                itemRender({
                    ...props.sidebarMenuButton,
                    isBtn: true,
                    className: classNames(props.sidebarMenuButton.className, props.hasOutsideIndents && "mr-12")
                }),
            React.createElement("div", { className: "stretched-to-fit overflow-hidden", ref: observableRef }, sidebarMenuItems && sidebarMenuItems.length && height ? (React.createElement(VirtualScrollList, { className: classNames("unselectable", props.hasOutsideIndents && styles.sidebar, props.virtualScrollListClassName), data: sidebarMenuItems, height: height, itemHeight: SIZES.MIN_ITEM_HEIGHT + SIZES.ITEMS_GAP, itemKey: "key", onScroll: handleScroll, ref: virtualScrollRef }, itemVirtualRender)) : (((_b = props.sidebarMenuItems) === null || _b === void 0 ? void 0 : _b.length) === 0 &&
                props.emptyText && (React.createElement(WelcomePanel, { message: props.emptyText, showIcon: false, messageMuted: props.bgColorDefault === "transparent", messageClassName: classNames("font-h5", props.bgColorDefault !== "transparent" && "text-default") })))))));
}
function SidebarMenuListInnerWithOverlaysProvider(props) {
    const Hoc = useMemo(() => {
        const SidebarMenuListT = (SidebarMenuListInner);
        return withOverlaysProvider(SidebarMenuListT);
    }, []);
    return React.createElement(Hoc, { ...props });
}
export default genericMemo(SidebarMenuListInnerWithOverlaysProvider);
